const connectedAiContent = {
  title: "Connect Claude or ChatGPT (Connected AI)",
  description: "Let Claude or ChatGPT search your Sortio knowledge graph from the web, read-only. Set it up in a couple of minutes.",
  sections: [
    {
      id: "overview",
      title: "What is Connected AI?",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          <strong>Connected AI</strong> lets web based assistants, <strong>Claude</strong> (claude.ai) and <strong>ChatGPT</strong> (chatgpt.com), search your Sortio <a href="/docs/spaces" class="text-[#06c]">knowledge graph</a>, read only. The assistant can find your files and the people, organizations, dates, and amounts inside them, then reason over that without you copying anything in.
        </p>

        <p class="text-[#1d1d1f] text-lg mb-6">
          It is opt in, off by default, and requires a paid plan (Pro or Team).
        </p>

        <div class="grid md:grid-cols-2 gap-4 my-8">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center"><span class="mr-2">🔒</span> Read only</h4>
            <p class="text-[#424245]">An assistant can never move, rename, or delete your files, and never reads their full contents. It only searches names, entities, and counts.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center"><span class="mr-2">💻</span> Stays on your machine</h4>
            <p class="text-[#424245]">Your knowledge graph lives on your computer. The assistant's request is answered locally; only the small result is returned. Nothing is reachable while Sortio is closed.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center"><span class="mr-2">🤝</span> Works with Claude and ChatGPT</h4>
            <p class="text-[#424245]">Add one connector in either product (or both). Support for more assistants is on the way.</p>
          </div>
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2 flex items-center"><span class="mr-2">🎚️</span> You control what is shared</h4>
            <p class="text-[#424245]">Exclude any sensitive person, organization, or amount from AI, and disconnect at any time.</p>
          </div>
        </div>
      `
    },
    {
      id: "how-it-works",
      title: "How it works",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          When you connect a device, Sortio holds a secure outbound connection to its relay. When you ask Claude or ChatGPT something, the assistant's request is routed to Sortio running on your machine, which answers from your local knowledge graph and returns only entity names, types, counts, and Space relative file names. The assistant receives that small result, not your full file contents. Nothing is reachable while Sortio is closed on that machine.
        </p>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2"><span class="mr-2">💡</span> Two halves</h4>
          <p class="text-[#424245]">Setup has two parts: <strong>connect the device</strong> in Sortio (once per machine), and <strong>add the Sortio connector</strong> in Claude or ChatGPT (once per assistant). Do them in any order.</p>
        </div>
      `
    },
    {
      id: "connect-device",
      title: "Step 1: Connect this device",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          This enrolls the machine that holds your files so it can answer assistant queries. You only do it once per computer.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0"><span class="text-white font-medium text-sm">1</span></div>
                Open Connected AI in Sortio
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Go to <strong>Integrations</strong> and open the <strong>AI Assistants</strong> section. (On older builds it lives under <strong>Settings</strong>, then <strong>Connected AI</strong>.)</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0"><span class="text-white font-medium text-sm">2</span></div>
                Click Connect this device
              </h5>
              <p class="mt-2 text-[#424245] ml-10">You may be asked to sign in. Connected AI requires a paid plan (Pro or Team). Once connected, the panel shows the device as sharing its knowledge graph.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "connect-claude",
      title: "Step 2: Add the connector in Claude",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Add Sortio as a custom connector in Claude so it can search your knowledge graph.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0"><span class="text-white font-medium text-sm">1</span></div>
                Open connectors in Claude
              </h5>
              <p class="mt-2 text-[#424245] ml-10">In <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" class="text-[#06c]">claude.ai</a>, open <strong>Customize</strong>, then <strong>Connectors</strong>, and choose <strong>Add custom connector</strong> — or jump straight to the <a href="https://claude.ai/customize/connectors?modal=add-custom-connector" target="_blank" rel="noopener noreferrer" class="text-[#06c]">Add custom connector</a> dialog.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0"><span class="text-white font-medium text-sm">2</span></div>
                Enter the Sortio URL
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Name it <strong>Sortio</strong> and paste the server URL:</p>
              <p class="mt-2 ml-10"><code class="bg-[#f5f5f7] px-2 py-1 rounded text-sm">https://mcp.getsortio.com/mcp</code></p>
              <p class="mt-2 text-[#424245] ml-10">Leave the OAuth fields blank. Click <strong>Add</strong>.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0"><span class="text-white font-medium text-sm">3</span></div>
                Sign in with Sortio
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Click <strong>Connect</strong>, then <strong>Sign in with Sortio</strong> and authorize read only access with your Sortio account. The connector turns to <strong>Connected</strong>.</p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0"><span class="text-white font-medium text-sm">4</span></div>
                Ask Claude to use Sortio
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Try: <em>"Use the Sortio MCP to show my knowledge graph stats and top entities."</em> Approve the tool when prompted.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "connect-chatgpt",
      title: "Step 2 (alternative): Add the connector in ChatGPT",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          ChatGPT uses the same Sortio connector. The exact menu names vary by ChatGPT plan and may require developer mode for custom connectors.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-6">
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0"><span class="text-white font-medium text-sm">1</span></div>
                Open Connectors in ChatGPT
              </h5>
              <p class="mt-2 text-[#424245] ml-10">In <a href="https://chatgpt.com" target="_blank" rel="noopener noreferrer" class="text-[#06c]">ChatGPT</a>, open <strong>Settings</strong>, then <strong>Connectors</strong>, and add a connector using the same URL: <code class="bg-[#f5f5f7] px-2 py-0.5 rounded text-sm">https://mcp.getsortio.com/mcp</code></p>
            </div>
            <div>
              <h5 class="font-medium text-[#1d1d1f] flex items-center">
                <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0"><span class="text-white font-medium text-sm">2</span></div>
                Sign in with Sortio
              </h5>
              <p class="mt-2 text-[#424245] ml-10">Authorize read only access with your Sortio account, then ask ChatGPT to search your Sortio knowledge graph.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "privacy",
      title: "Privacy and control",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Read only, always</h4>
              <p class="text-[#424245]">Connected assistants can search and read entity metadata only. They can never move, rename, delete, or read the full contents of a file.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Exclude specific entities</h4>
              <p class="text-[#424245]">Open any entity in the entity editor and turn on <strong>Exclude from AI</strong> to keep a particular person, organization, or amount out of what an assistant can see.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Disconnect at any time</h4>
              <p class="text-[#424245]">Use <strong>Disconnect</strong> in the AI Assistants panel. This revokes the device so it can no longer be used, even if a connector is still added on the AI side.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">What the AI provider sees</h4>
              <p class="text-[#424245]">Whatever an assistant searches is processed by that provider (Anthropic or OpenAI), the same as anything else you type into it. Sortio sends only entity names, types, counts, and Space relative file names, not full file contents.</p>
            </div>
          </div>
        </div>
      `
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="border-b border-[#e0e0e5] p-5"><h4 class="font-semibold text-[#1d1d1f]">Common issues</h4></div>
          <div class="p-6 space-y-6">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">"No Sortio device is online"</h4>
              <p class="text-[#424245]">The assistant connected, but Sortio is not running on the machine you enrolled. Open Sortio on that computer and try again. Queries only work while Sortio is running there.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">"Requires a paid plan"</h4>
              <p class="text-[#424245]">Connected AI is available on Pro and Team. Upgrade from <a href="/pricing" class="text-[#06c]">Pricing</a>, then connect the device again.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">The assistant cannot find a recent file</h4>
              <p class="text-[#424245]">Connected AI searches your knowledge graph, so a file needs to be indexed first. Add its folder as a <a href="/docs/spaces" class="text-[#06c]">Space</a> and let it index.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">I switched accounts on the desktop</h4>
              <p class="text-[#424245]">Restart Sortio after switching accounts so it reconnects with the new account's credentials, then reconnect the connector on the AI side.</p>
            </div>
          </div>
        </div>

        <div class="mt-8 border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] mb-3">Related documentation</h4>
          <ul class="list-disc list-inside text-[#424245] space-y-1 ml-2">
            <li><a href="/docs/spaces" class="text-[#06c]">Spaces</a>, the indexed folders that build your knowledge graph</li>
            <li><a href="/docs/security" class="text-[#06c]">Security and Privacy</a>, how Sortio protects your data on disk</li>
            <li><a href="/docs/byok" class="text-[#06c]">Bring Your Own Key</a>, use your own OpenAI or Anthropic key inside Sortio</li>
          </ul>
        </div>
      `
    }
  ]
};

export default connectedAiContent;
